import { money } from "./money";

/**
 * Sales tax on a charge, as every screen shows it.
 *
 * The server prices each charge with its tax LINES (usage.services.tax): one
 * per tax the office pays — "HST", or "GST" + "PST" — each with its own rate and
 * amount, and each shown on its own row. Pure, so the wording is testable.
 *
 * A payload from before tax lines existed (a page loaded before the deploy, a
 * receipt row stamped earlier) carries one tax under one label; it reads as one
 * line, so nothing downstream needs to know which it got.
 */

const cents = (value) => Math.round(Number(value || 0) * 100);
const dollars = (c) => (c / 100).toFixed(2);

/** `[{label, percent, amount}]` as strings; `[]` when the charge carries no tax. */
export function taxLinesOf(charge) {
  if (!charge) return [];
  if (Array.isArray(charge.tax_lines)) {
    return charge.tax_lines.map((line) => ({
      label: String(line.label || ""),
      percent: line.percent != null ? String(line.percent) : "",
      amount: String(line.amount != null ? line.amount : "0"),
    }));
  }
  if (!(Number(charge.tax) > 0)) return [];
  return [{
    label: charge.tax_label || "Tax",
    percent: charge.tax_percent != null ? String(charge.tax_percent) : "",
    amount: String(charge.tax),
  }];
}

/**
 * A tax row's label. One tax reads as it always has ("HST"); with several, each
 * names its rate ("GST 5%") so the rows can be told apart.
 */
export function taxLineLabel(line, count) {
  return count > 1 && line.percent ? `${line.label} ${line.percent}%` : line.label;
}

/** "HST $64.87" / "GST $24.95 + PST $34.93" / "" — the tax in one phrase. */
export function taxLinesPhrase(lines) {
  return (lines || []).map((line) => `${taxLineLabel(line, lines.length)} $${money(line.amount)}`).join(" + ");
}

/**
 * The same tax summed over several charges (a cart of offices): one row per
 * (label, rate), so BC's PST 7% and Saskatchewan's PST 6% stay apart. Summed in
 * cents.
 */
export function sumTaxLines(lineLists) {
  const order = [];
  const byKey = {};
  (lineLists || []).forEach((lines) => (lines || []).forEach((line) => {
    const key = `${line.label}|${line.percent}`;
    if (!byKey[key]) {
      byKey[key] = { label: line.label, percent: line.percent, cents: 0 };
      order.push(key);
    }
    byKey[key].cents += cents(line.amount);
  }));
  return order.map((key) => ({ label: byKey[key].label, percent: byKey[key].percent, amount: dollars(byKey[key].cents) }));
}
